import { useEffect, useRef } from "react";
import type { GeneralsReplay } from "./generals-board-model";
import { createGeneralsRenderer } from "./generals-board-renderer";
import { seedGeneralsSimulation } from "./seed-generals-simulation";
import { tickGeneralsSimulation } from "./generals-board-simulation";
import { replayBoard, applyReplayFrame } from "./generals-replay-board";

const frameMs = 140;

export function useGeneralsBoard({
  replays,
  size,
  replayFadeMs,
}: {
  readonly replays: readonly (GeneralsReplay | null)[];
  readonly size: number;
  readonly replayFadeMs: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const latestReplays = useRef<readonly GeneralsReplay[]>([]);
  const ready = replays.length > 0;
  const hasReplay = replays.some((replay) => replay !== null);
  useEffect(() => {
    latestReplays.current = replays.filter((replay) => replay !== null);
  }, [replays]);

  useEffect(() => {
    if (!ref.current || !ready) return;

    const root = ref.current;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timers: number[] = [];
    let disposed = false;
    let ticker: number | null = null;
    const renderer = createGeneralsRenderer({ root, size });
    function finishCycle(next: () => void) {
      if (ticker) window.clearInterval(ticker);

      ticker = null;
      root.style.opacity = "0";
      timers.push(
        window.setTimeout(() => {
          if (!disposed) next();
        }, replayFadeMs),
      );
    }
    function play(index: number) {
      const available = latestReplays.current;
      const replay = available[index % available.length];

      if (!replay)
        throw new RangeError("Replay selection requires an available replay");

      const startAt = Math.floor(
        replay.frames.length * (0.35 + Math.random() * 0.3),
      );
      const board = replayBoard({ replay, startAt });
      renderer.build(board);
      renderer.render();
      root.style.opacity = "1";

      if (still) return;

      const frames = replay.frames.slice(startAt).values();
      ticker = window.setInterval(() => {
        const frame = frames.next();

        if (frame.done) {
          finishCycle(() => play(latestReplays.current.indexOf(replay) + 1));
          return;
        }

        applyReplayFrame({ tiles: board.tiles, frame: frame.value });
        renderer.render();
      }, frameMs);
    }
    function playSimulation() {
      const simulation = seedGeneralsSimulation();

      // Start mid-match so the first paint includes both colours.
      for (let i = 0; i < 120; i++) tickGeneralsSimulation(simulation);

      renderer.build(simulation);
      renderer.render();
      root.style.opacity = "1";

      if (still) return;

      ticker = window.setInterval(() => {
        const fell = tickGeneralsSimulation(simulation);
        renderer.render();

        if (fell || simulation.tickCount > 700) finishCycle(playSimulation);
      }, 110);
    }
    root.style.opacity = "0";

    if (hasReplay)
      play(Math.floor(Math.random() * latestReplays.current.length));
    else playSimulation();

    return () => {
      disposed = true;

      if (ticker) window.clearInterval(ticker);

      for (const timer of timers) window.clearTimeout(timer);

      root.replaceChildren();
    };
  }, [ready, hasReplay, size, replayFadeMs]);
  return ref;
}
